/**
 * AI Insights Service
 * ============================================================================
 *
 * This service is the integration point between the dashboard UI and the
 * AI insights generator. It is deliberately structured as an `async` function
 * returning a Promise, matching the shape of a real API call, so that the
 * production wiring is a one-line change with zero downstream impact.
 *
 * ----------------------------------------------------------------------------
 * CURRENT BEHAVIOUR (Demo / Pre-seed)
 * ----------------------------------------------------------------------------
 *
 * `generateInsights(tenant)` reads the pre-baked `aiInsights` block from the
 * tenant data file (`src/data/tenants.js`) and returns it after an artificial
 * latency that mimics LLM streaming behaviour. This is intentional for two
 * reasons:
 *
 *   1. Demos must be deterministic. Live LLM calls fail in front of investors
 *      at the worst possible moments. Pre-baked content cannot fail.
 *   2. Each pre-baked insight has been crafted in formal exec register and
 *      stress-tested for narrative coherence with its tenant scenario. A
 *      naive prompt-and-response cycle is unlikely to match this quality
 *      without significant prompt engineering.
 *
 * ----------------------------------------------------------------------------
 * PRODUCTION SWAP (Post-seed, when a CTO joins)
 * ----------------------------------------------------------------------------
 *
 * To switch to a live Claude API call, replace the body of `generateInsights`
 * with the implementation below. Nothing in the AIInsightsPanel component or
 * elsewhere needs to change, the function signature and return shape are
 * already correct.
 *
 *   ```js
 *   export async function generateInsights(tenant) {
 *     const response = await fetch('/api/insights', {
 *       method: 'POST',
 *       headers: { 'Content-Type': 'application/json' },
 *       body: JSON.stringify({ tenantId: tenant.id, metrics: tenant.metrics }),
 *     });
 *     if (!response.ok) throw new InsightsError(response.statusText);
 *     return response.json();
 *   }
 *   ```
 *
 * The `/api/insights` endpoint should:
 *   1. Validate the request and authorise the user against the tenant
 *   2. Construct the prompt (template below)
 *   3. Call Anthropic's API server-side using a key from process.env
 *   4. Parse the JSON response and return { headline, body, recommendations }
 *
 * Anthropic API key MUST live server-side. Embedding it in the browser is a
 * Terms of Service violation and a financial liability, anyone with browser
 * DevTools can extract it.
 *
 * ----------------------------------------------------------------------------
 * RECOMMENDED PROMPT TEMPLATE
 * ----------------------------------------------------------------------------
 *
 * System: You are a senior pay equity analyst preparing executive briefings
 * for HR leaders on EU Pay Transparency Directive 2023/970 compliance. Your
 * register is formal, diagnostic, and cause-focused. You cite specific
 * Articles when statutory obligations are engaged. You avoid casual phrasing.
 * Output strictly the JSON schema requested, no preamble, no markdown.
 *
 * User: Analyse the following pay equity metrics for {tenant.name}
 * ({tenant.industry}, {tenant.country}, {tenant.employees} workers,
 * reporting year {tenant.reportingYear}). Return JSON with three fields:
 *   - headline: one sentence, the principal finding
 *   - body: 3–5 sentences, structural diagnosis with cause-and-effect
 *   - recommendations: 4–6 strings, ordered by priority, each citing the
 *     Article engaged where applicable
 *
 * Metrics: {JSON.stringify(tenant.metrics)}
 * Categories of workers: {JSON.stringify(tenant.categoriesOfWorkers)}
 * Quartiles: {JSON.stringify(tenant.quartiles)}
 * Active assessments: {JSON.stringify(tenant.assessmentAlerts)}
 *
 * ----------------------------------------------------------------------------
 * SUGGESTED MODEL CONFIGURATION
 * ----------------------------------------------------------------------------
 *
 * model:        'claude-opus-4-7'  (Opus for the analytical depth)
 * max_tokens:   1500
 * temperature:  0.3                 (Low temp for analytical consistency)
 * Streaming:    Yes, use SSE so the UI renders progressively
 *
 * Approximate cost per call at Opus 4.7 pricing: ~$0.05–$0.08.
 * For demo use, expect 5–20 calls per session. Budget accordingly.
 */

import { TENANTS } from '../data/tenants';

/**
 * Custom error type so the UI can distinguish API failures from other issues
 * and render a useful empty state.
 */
export class InsightsError extends Error {
  constructor(message, code = 'INSIGHTS_ERROR') {
    super(message);
    this.name = 'InsightsError';
    this.code = code;
  }
}

/**
 * Simulated network/streaming latency. Matches the 800–2400ms p50/p95 range
 * observed for Claude Opus on prompts of this size, so the UX feels
 * authentic when you swap in the real API.
 */
const simulatedLatency = (forced) => {
  if (forced != null) return forced;
  // Variable: feels less suspicious than a fixed value
  return 800 + Math.random() * 1600;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetch AI-generated insights for the given tenant.
 *
 * @param {object} tenant         The tenant object from src/data/tenants.js
 * @param {object} [options]
 * @param {number} [options.latencyMs]  Override simulated latency (testing)
 * @param {boolean} [options.failOnce]  Force a single failure (testing)
 *
 * @returns {Promise<{ headline: string, body: string, recommendations: string[], meta: object }>}
 */
export async function generateInsights(tenant, options = {}) {
  if (!tenant || !tenant.id) {
    throw new InsightsError('Tenant required', 'INVALID_TENANT');
  }

  // PRODUCTION SWAP POINT, replace everything below with the fetch() block
  // documented at the top of this file.

  await sleep(simulatedLatency(options.latencyMs));

  if (options.failOnce) {
    throw new InsightsError('Simulated failure', 'SIMULATED');
  }

  // Pick language-specific insights. Falls back to English if the requested
  // language doesn't have a translation.
  const lang = options.lang || 'en';
  const tenantData = TENANTS[tenant.id];
  const source = (lang === 'de' && tenantData?.aiInsightsDe)
    ? tenantData.aiInsightsDe
    : tenantData?.aiInsights;
  if (!source) {
    throw new InsightsError(`No insights available for tenant ${tenant.id}`, 'NOT_FOUND');
  }

  // Return shape mirrors what the production endpoint should return
  return {
    headline: source.headline,
    body: source.body,
    recommendations: source.recommendations,
    meta: {
      model: 'claude-opus-4-7',
      tenantId: tenant.id,
      generatedAt: new Date().toISOString(),
      // The real endpoint will include token usage; pre-bake reports the
      // approximate token count of the response so the UI can show it.
      tokenEstimate: estimateTokens(source),
    },
  };
}

/**
 * Fetch AI-generated insights via a live call to the Anthropic API.
 *
 * Calls Claude Opus 4.7 directly from the browser using VITE_ANTHROPIC_API_KEY.
 * Same return shape as generateInsights(). On any failure (no key, network,
 * rate limit, API error, malformed response), silently falls back to the
 * pre-baked content via generateInsights() so the panel never breaks during
 * a live demo.
 *
 * SECURITY NOTE: VITE_ANTHROPIC_API_KEY ships in the browser bundle. This is
 * acceptable for pre-seed demo phase with a low monthly spend cap on the key,
 * but must move to a server-side proxy before customer launch.
 */
export async function generateInsightsLive(tenant, options = {}) {
  if (!tenant || !tenant.id) {
    throw new InsightsError('Tenant required', 'INVALID_TENANT');
  }

  const apiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY;
  if (!apiKey) {
    return generateInsights(tenant, options);
  }

  const lang = options.lang || 'en';
  const langInstruction = lang === 'de'
    ? 'Respond in German (formal register, sie-form).'
    : lang === 'fr' ? 'Respond in French (formal register, vous-form).'
    : lang === 'it' ? 'Respond in Italian (formal register, Lei-form).'
    : lang === 'es' ? 'Respond in Spanish (formal register, usted-form).'
    : 'Respond in English.';

  const systemPrompt = 'You are a senior pay equity analyst preparing executive briefings for HR leaders on EU Pay Transparency Directive 2023/970 compliance. Your register is formal, diagnostic, and cause-focused. You cite specific Articles when statutory obligations are engaged. You avoid casual phrasing, contractions, em dashes, and hyphens in user-facing copy. ' + langInstruction + ' Output strictly the JSON schema requested, no preamble, no markdown.';

  const userPrompt = 'Analyse the following pay equity metrics for ' + tenant.name + ' (' + (tenant.industry || 'unknown industry') + ', ' + (tenant.country || 'unknown country') + ', ' + (tenant.employees || 'unknown') + ' workers).\n\n' +
    'Return JSON with exactly three fields:\n' +
    '- headline: one sentence, the principal finding\n' +
    '- body: three to five sentences, structural diagnosis with cause and effect\n' +
    '- recommendations: four to six strings, ordered by priority, each citing the Article engaged where applicable\n\n' +
    'Metrics: ' + JSON.stringify(tenant.metrics || {}) + '\n' +
    'Categories of workers: ' + JSON.stringify(tenant.categoriesOfWorkers || []) + '\n' +
    'Quartiles: ' + JSON.stringify(tenant.quartiles || []) + '\n' +
    'Active assessments: ' + JSON.stringify(tenant.assessmentAlerts || []);

  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model: 'claude-opus-4-7',
        max_tokens: 1500,
        system: systemPrompt,
        messages: [{ role: 'user', content: userPrompt }],
      }),
    });

    if (!response.ok) {
      console.warn('[aiInsights] Live API call failed, status:', response.status);
      return generateInsights(tenant, options);
    }

    const data = await response.json();
    const text = data?.content?.[0]?.text || '';
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.warn('[aiInsights] No JSON found in Claude response, falling back to mock');
      return generateInsights(tenant, options);
    }

    const parsed = JSON.parse(jsonMatch[0]);
    if (!parsed.headline || !parsed.body || !Array.isArray(parsed.recommendations)) {
      console.warn('[aiInsights] Malformed Claude response, falling back to mock');
      return generateInsights(tenant, options);
    }

    return {
      headline: parsed.headline,
      body: parsed.body,
      recommendations: parsed.recommendations,
      meta: {
        model: data?.model || 'claude-opus-4-7',
        tenantId: tenant.id,
        generatedAt: new Date().toISOString(),
        tokenEstimate: (data?.usage?.input_tokens || 0) + (data?.usage?.output_tokens || 0),
        live: true,
      },
    };
  } catch (err) {
    console.warn('[aiInsights] Live call threw, falling back to mock:', err?.message);
    return generateInsights(tenant, options);
  }
}

/** Rough token count: ~4 chars per token. Good enough for UI display. */
function estimateTokens(insights) {
  const total = insights.headline.length
    + insights.body.length
    + insights.recommendations.join(' ').length;
  return Math.round(total / 4);
}
